import React, { RefObject } from "react";
import { Field, Form, Formik, FormikConfig } from "formik";
import { Button, Modal, ModalBody, ModalHeader } from "reactstrap";
import { useLogin } from "../hooks/useLogin";
import { Alert } from "../../interface/alert.interface";
import { useBoolean } from "../../custom-hooks/useBoolean";
import { ReactComponent as IconClose } from "../../assets/img/icon/icon_cerrar_verde.svg";
import { TotpInput } from "./TotpInput";

const DIGITS_LENGTH = 6;

export interface TotpValidationFormProps {
    needConfiguration: boolean;
    username: string;
    password: string;
    qrCode?: string;
    daysToExpireCookie: number;
    alert: RefObject<Alert>;
}

interface TotpValues {
    totpCode: string;
    trustDevice: boolean;
}

export const TotpValidationModal: React.FC<TotpValidationFormProps> = ({
    needConfiguration,
    username,
    password,
    qrCode,
    daysToExpireCookie,
    alert,
}) => {
    const { requestAuthentication } = useLogin({ alert });
    const { value: showInput, setTrue: inputOn, setFalse: inputOff } = useBoolean();

    const handleVerifyTotp: FormikConfig<TotpValues>["onSubmit"] = (values, formikHelpers) => {
        requestAuthentication(username, password, {
            "X-TOTP-CODE": values.totpCode,
            "X-TRUST-DEVICE": values.trustDevice,
        }).finally(() => formikHelpers.setSubmitting(false));
    };

    return (
        <Modal isOpen centered>
            <ModalHeader className={"position-relative"}>
                {needConfiguration ? "Configuración de código de verificación" : "Código de verificación"}
                <a href="/" className={"d-flex position-absolute"} style={{ top: "10px", right: "10px" }}>
                    <IconClose width={14} height={14} />
                </a>
            </ModalHeader>
            <ModalBody className={"pb-3"} style={{ lineHeight: "1.25" }}>
                {needConfiguration && !showInput ? (
                    <div className="d-flex flex-column gap-4">
                        <img
                            src={`data:image/png;base64,${qrCode}`}
                            alt={"Código Qr"}
                            height={200}
                            width={200}
                            className={"d-block m-auto"}
                        />
                        <p className={"m-0"}>
                            Este método de acceso está habilitado para su usuario. Debe realizar esta configuración solo
                            una vez.
                        </p>
                        <p className={"m-0"}>Siga estos pasos:</p>
                        <ol className={"m-0"}>
                            <li className={"pb-2"}>
                                <span className={"fw-semibold"}>Escanee el código QR</span> con una aplicación de
                                autenticación en su celular (por ejemplo, Google Authenticator, Authy, entre otras).
                            </li>
                            <li>
                                Haga clic en <span className={"fw-semibold"}>continuar</span>.
                            </li>
                        </ol>
                        <div className={"ml-auto"}>
                            <Button color={"primary"} type="button" onClick={inputOn} round>
                                Continuar
                            </Button>
                        </div>
                    </div>
                ) : (
                    <Formik<TotpValues>
                        initialValues={{ totpCode: "", trustDevice: false }}
                        onSubmit={handleVerifyTotp}
                    >
                        {({ isSubmitting, values, setFieldValue }) => (
                            <Form>
                                <div className="d-flex flex-column gap-4">
                                    <ol className={"m-0"}>
                                        <li className={"pb-2"}>
                                            Ingrese el <span className={"fw-semibold"}>código de 6 dígitos</span>{" "}
                                            generado por la aplicación de autenticación.
                                        </li>
                                        <li>
                                            Presione <span className={"fw-semibold"}>Verificar</span>.
                                        </li>
                                    </ol>
                                    <TotpInput
                                        isRequired
                                        value={values.totpCode}
                                        onChange={totp => setFieldValue("totpCode", totp)}
                                    />
                                    <label className="mx-auto">
                                        <Field name="trustDevice" type="checkbox" />
                                        <span className="ml-1">
                                            Confiar en este dispositivo por {daysToExpireCookie} días
                                        </span>
                                    </label>
                                    <div className={"d-flex justify-content-end gap-2"}>
                                        {needConfiguration && (
                                            <Button
                                                className="text-primary"
                                                type="button"
                                                onClick={inputOff}
                                                round
                                                disabled={isSubmitting}
                                            >
                                                Atrás
                                            </Button>
                                        )}
                                        <Button
                                            color={"primary"}
                                            type={"submit"}
                                            round
                                            disabled={isSubmitting || values.totpCode.length != DIGITS_LENGTH}
                                        >
                                            Verificar
                                        </Button>
                                    </div>
                                </div>
                            </Form>
                        )}
                    </Formik>
                )}
            </ModalBody>
        </Modal>
    );
};
